<template>
  <div class="container-fluid py-4 gray-bg">

    <!-- Otsikko ja Päivitysnappi -->
    <div class="d-flex justify-content-between align-items-center mb-4 bg-white p-3 shadow-sm rounded">
      <h4 class="mb-0 font-weight-bold text-dark">
        <i class="fas fa-sms mr-2 text-primary"></i>SMS-kustannusanalyysi
      </h4>
      <div class="d-flex align-items-center">
        <div class="btn-group mr-2" role="group">
          <button
            v-for="d in [7, 30, 90]"
            :key="d"
            @click="setDays(d)"
            class="btn btn-sm rounded-pill mr-1"
            :class="days === d ? 'btn-primary' : 'btn-outline-primary'"
          >{{ d }} pv</button>
        </div>
        <button @click="fetchData" class="btn btn-outline-primary btn-sm rounded-pill" :disabled="loading">
          <i class="fas fa-sync-alt mr-1" :class="{'fa-spin': loading}"></i>
          {{ loading ? 'Ladataan...' : 'Päivitä' }}
        </button>
      </div>
    </div>

    <!-- Latausilmoitus -->
    <div v-if="loading && !stats" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted">Haetaan SMS-kustannuksia...</p>
    </div>

    <!-- Pääsisältö -->
    <div v-if="stats">

      <!-- Kokonaiskustannus -->
      <div class="row mb-4">
        <div class="col-md-4">
          <div class="card border-left-green shadow-sm h-100 py-2">
            <div class="card-body">
              <div class="row no-gutters align-items-center">
                <div class="col mr-2">
                  <div class="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Kustannus ({{ stats.summary.currency }})
                  </div>
                  <div class="h5 mb-0 font-weight-bold text-gray-800">
                    {{ formatCurrency(stats.summary.total_cost, stats.summary.currency) }}
                  </div>
                </div>
                <div class="col-auto">
                  <i class="fas fa-euro-sign fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Paivakohtainen kustannus -->
      <div class="card shadow-sm mb-4" v-if="dailyChartData.length">
        <div class="card-header py-3 bg-indigo text-white">
          <h6 class="m-0 font-weight-bold">Kustannus päivittäin ({{ days }} pv)</h6>
        </div>
        <div class="card-body">
          <div style="height: 300px;">
            <BarChartBasic
              :key="days"
              :data="dailyChartData"
              :label="'Kustannus (' + stats.summary.currency + ')'"
              height="300"
              :show-trend="true"
              trend-label="Trendi"
            />
          </div>
        </div>
      </div>

      <!-- Taulukko (maakohtainen erittely) -->
      <div class="card shadow-sm mb-4">
        <div class="card-header py-3 bg-indigo text-white">
          <h6 class="m-0 font-weight-bold">Maakohtainen erittely ({{ days }} pv)</h6>
        </div>
        <div class="card-body p-0 text-dark">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead class="bg-light">
                <tr>
                  <th class="border-top-0">Maa</th>
                  <th class="border-top-0">SKU-kuvaus</th>
                  <th class="border-top-0 text-right">Kustannus</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in stats.countries" :key="index">
                  <td class="align-middle">
                    <span class="badge badge-secondary">{{ row.country }}</span>
                  </td>
                  <td class="align-middle text-muted">{{ row.sku_description }}</td>
                  <td class="align-middle text-right text-primary font-weight-bold">
                    {{ formatCurrency(row.cost, row.currency) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import BarChartBasic from '@/components/BarChartBasic';

export default {
  components: { BarChartBasic },
  data() {
    return {
      loading: false,
      stats: null,
      days: 30,
    };
  },
  computed: {
    dailyChartData() {
      if (!this.stats || !this.stats.daily) return [];
      return this.stats.daily.map(d => ({ eventday: d.date, eventcount: d.cost }));
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    setDays(d) {
      this.days = d;
      this.fetchData();
    },
    fetchData() {
      this.loading = true;
      axios.get('/1.0/front/sms/costs', { params: { days: this.days } })
        .then(response => {
          this.stats = response.data;
        })
        .catch(error => {
          console.error("Virhe ladattaessa SMS-kustannuksia:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatCurrency(value, currency) {
      if (!value) return new Intl.NumberFormat('fi-FI', { style: 'currency', currency: currency || 'EUR' }).format(0);
      return new Intl.NumberFormat('fi-FI', {
        style: 'currency',
        currency: currency || 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 4
      }).format(value);
    }
  }
};
</script>

<style scoped>
.gray-bg { background-color: #f8f9fc; min-height: 100vh; }
.bg-indigo { background-color: #4e73df; }
.text-xs { font-size: .7rem; }

.border-left-green { border-left: .25rem solid #1cc88a !important; }

.table thead th {
  font-size: 0.8rem;
  text-transform: none;
  color: #858796;
  font-weight: 600;
  border-bottom: none;
}
.table td {
  border-top: 1px solid #e3e6f0;
  vertical-align: middle;
}
</style>
